
import React from 'react';
import { X, AlertTriangle, Info, CheckCircle } from 'lucide-react';

interface Props {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning' | 'danger' | 'success';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<Props> = ({ 
  isOpen, 
  title, 
  message, 
  confirmText = 'OK', 
  cancelText = 'キャンセル', 
  type = 'warning',
  onConfirm, 
  onCancel 
}) => {
  if (!isOpen) return null;

  const typeStyles = {
    info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50', button: 'bg-blue-600 hover:bg-blue-700' },
    warning: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50', button: 'bg-orange-600 hover:bg-orange-700' },
    danger: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', button: 'bg-red-600 hover:bg-red-700' },
    success: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', button: 'bg-green-600 hover:bg-green-700' }
  };

  const style = typeStyles[type];
  const Icon = style.icon;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-xs">
      <div className="bg-white rounded shadow-2xl w-full max-w-xs p-5 relative animate-in fade-in zoom-in duration-150">
        <button onClick={onCancel} className="absolute top-3 right-3 text-gray-300 hover:text-gray-500">
          <X size={16} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className={`p-2.5 rounded-full ${style.bg} mb-3`}>
            <Icon size={24} className={style.color} />
          </div>
          
          <h3 className="text-sm font-black text-gray-800 mb-1.5">{title}</h3>
          <p className="text-gray-500 mb-5 text-[11px] whitespace-pre-wrap leading-relaxed">{message}</p>

          <div className="flex gap-2 w-full">
            <button
              onClick={onCancel}
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-xs text-gray-600 hover:bg-gray-50 transition-colors font-bold"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 px-3 py-1.5 text-white rounded font-black text-xs transition-colors shadow-sm ${style.button}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
